import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Paper,
  Button
} from '@mui/material';
import { format, isSameDay, parseISO } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from '../utils/axios';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/bookings');
        setBookings(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateTotalPrice = (booking) => {
    const hours = (new Date(booking.endTime) - new Date(booking.startTime)) / (1000 * 60 * 60);
    return booking.gymId?.pricePerHour * hours || 0;
  };

  const isWithin24Hours = (booking) => {
    const now = new Date();
    const bookingStart = new Date(booking.startTime);
    const hoursUntilBooking = (bookingStart - now) / (1000 * 60 * 60);
    return hoursUntilBooking < 24 && hoursUntilBooking > 0;
  };

  const getBookingsForDate = (date) => {
    return bookings.filter(booking => 
      isSameDay(parseISO(booking.startTime), date)
    );
  };

  const handleViewChange = (event, newView) => {
    setView(newView);
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      const booking = bookings.find(b => b._id === bookingId);
      
      // Check if this is a late cancellation
      if (booking && isWithin24Hours(booking)) {
        const cancellationFee = calculateTotalPrice(booking) * 0.25;
        const confirmed = window.confirm(
          `⚠️ Late Cancellation Warning\n\n` +
          `Cancelling this booking will incur a 25% fee of $${cancellationFee.toFixed(2)}.\n\n` +
          `Are you sure you want to proceed with the cancellation?`
        );
        
        if (!confirmed) {
          return;
        }
      }
      
      const response = await axios.patch(`/api/bookings/${bookingId}/status`, { status: 'cancelled' });
      
      // Update the booking in the state
      setBookings(bookings.map(booking =>
        booking._id === bookingId ? { ...booking, ...response.data } : booking
      ));
      
      // Show success message with cancellation fee info if applicable
      if (response.data.cancellationFee) {
        alert(`Booking cancelled. Late cancellation fee of $${response.data.cancellationFee.toFixed(2)} has been charged.`);
      } else {
        alert('Booking cancelled successfully. No cancellation fee applied.');
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      if (err.response?.data?.requiresPaymentMethod) {
        if (err.response?.data?.incompletePaymentMethod) {
          setError('Your payment method information is incomplete. Please re-add your payment method in the payment settings.');
        } else {
          setError('Payment method required for late cancellation. Please add a payment method first.');
        }
      } else if (err.response?.data?.paymentError) {
        setError(`Cancellation failed: ${err.response.data.message}`);
      } else {
        setError('Failed to cancel booking');
      }
    }
  };

  const handleSendSuccessEmail = async (bookingId) => {
    try {
      const response = await axios.post(`/api/bookings/${bookingId}/send-success-email`);
      alert(response.data.message);
    } catch (err) {
      console.error('Error sending success email:', err);
      setError('Failed to send success email');
    }
  };

  const handleContactSupport = async (bookingId) => {
    try {
      const response = await axios.post(`/api/bookings/${bookingId}/contact-support`);
      alert(response.data.message);
    } catch (err) {
      console.error('Error contacting support:', err);
      setError('Failed to contact support');
    }
  };

  const renderDay = (day, selectedDays, pickersDayProps) => {
    const hasBookings = bookings.some(booking => 
      isSameDay(parseISO(booking.startTime), day)
    );

    return (
      <Box
        {...pickersDayProps}
        sx={{
          ...(pickersDayProps?.sx || {}),
          ...(hasBookings && {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            '&.Mui-selected': {
              backgroundColor: 'primary.dark',
            },
          }),
        }}
      />
    );
  };

  const renderBookingCard = (booking) => (
    <Card key={booking._id} sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {booking.gymId?.photos?.[0] && (
              <Box
                component="img"
                src={booking.gymId.photos[0]}
                alt={booking.gymId.name}
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 1
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              {booking.gymId?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {format(new Date(booking.startTime), 'MMM d, yyyy h:mm a')} - {format(new Date(booking.endTime), 'h:mm a')}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={booking.status.toUpperCase()}
                color={getStatusColor(booking.status)}
                size="small"
              />
              <Chip
                label={`$${calculateTotalPrice(booking).toFixed(2)}`}
                color="primary"
                size="small"
              />
            </Box>
            {/* Cancel button for future, non-cancelled bookings */}
            {booking.status !== 'cancelled' && new Date(booking.startTime) > new Date() && (
              <Box sx={{ mt: 2 }}>
                {/* Cancellation policy warning */}
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {isWithin24Hours(booking) ? (
                      <>
                        <strong>⚠️ Late Cancellation Warning:</strong> Cancelling now will incur a 25% fee (${(calculateTotalPrice(booking) * 0.25).toFixed(2)}).
                      </>
                    ) : (
                      <>
                        <strong>Cancellation Policy:</strong> Cancellations within 24 hours of booking time will incur a 25% fee.
                      </>
                    )}
                  </Typography>
                </Alert>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleCancelBooking(booking._id)}
                  >
                    Cancel Booking
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    size="small"
                    onClick={() => handleSendSuccessEmail(booking._id)}
                  >
                    Send Success Email
                  </Button>
                </Box>
              </Box>
            )}
            {/* Support contact button for all bookings */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleContactSupport(booking._id)}
              >
                Contact Support
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Booked on {format(new Date(booking.createdAt), 'MMM d, yyyy')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Bookings
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={view} onChange={handleViewChange}>
          <Tab label="List View" value="list" />
          <Tab label="Calendar View" value="calendar" />
        </Tabs>
      </Box>

      {bookings.length === 0 ? (
        <Alert severity="info">You haven't made any bookings yet.</Alert>
      ) : view === 'list' ? (
        <Grid container spacing={3}>
          {bookings.map(booking => (
            <Grid item xs={12} key={booking._id}>
              {renderBookingCard(booking)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                My Bookings Calendar
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={setSelectedDate}
                  slots={{
                    day: renderDay
                  }}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>
              Bookings for {format(selectedDate, 'MMMM d, yyyy')}
            </Typography>
            {getBookingsForDate(selectedDate).length === 0 ? (
              <Alert severity="info">No bookings for this date.</Alert>
            ) : (
              getBookingsForDate(selectedDate).map(booking => renderBookingCard(booking))
            )}
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Bookings; 